import { create } from 'zustand';
import todosService from '../api/services/todos.service';

/* The class checklist, straight from /roomParentAdmin/todos. Shared by the
   To-Do page and the dashboard card so the two never disagree. Writes refetch
   the list, because the server is what decides a task's group. */

export const GENERAL = 'General';

/* Where a task sits in its group is decided once, when the list is fetched:
   still-to-do first by due date, done last, undated at the end. Ticking a box
   deliberately does NOT re-sort — a task that dropped to the bottom the instant
   you checked it made the list impossible to follow. The order is recomputed on
   the next load, which every other write already triggers.

   `seq` is a rank, not a reordering: the array stays in the order the API sent
   it so the groups themselves keep their own order. */
const withDisplayOrder = (todos) => {
  const ranked = [...todos].sort((a, b) => {
    if (a.done !== b.done) return a.done ? 1 : -1;
    return (a.due || '9999-12-31').localeCompare(b.due || '9999-12-31');
  });
  const seq = new Map(ranked.map((t, i) => [t.id, i]));
  return todos.map((t) => ({ ...t, seq: seq.get(t.id) ?? 0 }));
};

const useTodosStore = create((set, get) => ({
  todos: [],
  loading: false,
  /* which network the loaded list belongs to — null until the first load */
  todosNetworkId: null,

  load: async (networkId, { force = false } = {}) => {
    if (!networkId) return;
    if (!force && get().todosNetworkId === networkId) return;
    set({ loading: true });
    try {
      const r = await todosService.list(networkId, { page: 1, limit: 100 });
      set({ todos: withDisplayOrder(r.todos), todosNetworkId: networkId });
    } finally {
      set({ loading: false });
    }
  },

  addTodo: async (networkId, draft) => {
    await todosService.create({ networkId, ...draft });
    await get().load(networkId, { force: true });
  },

  editTodo: async (networkId, todoId, patch) => {
    await todosService.update({ todoId, ...patch });
    await get().load(networkId, { force: true });
  },

  /* The checkbox. Completion is per user, so this records or removes *my* mark
     via PATCH /todo/done; the server decides from that whether the task counts
     as done. The row flips straight away and the reply is then applied as the
     truth — which matters when reopening a task a second room parent has also
     ticked off, because that one stays done.

     Returns the server's completion so the caller can tell the difference. */
  toggleTodo: async (networkId, todo) => {
    const isDone = !todo.done;
    const patch = (fields) =>
      set({ todos: get().todos.map((t) => (t.id === todo.id ? { ...t, ...fields } : t)) });

    patch({
      done: isDone,
      doneByMe: isDone,
      doneCount: Math.max(0, (todo.doneCount || 0) + (isDone ? 1 : -1)),
      status: isDone ? 'completed' : 'pending',
    });
    try {
      const result = await todosService.setDone({ todoId: todo.id, isDone });
      patch(result);
      return result;
    } catch (err) {
      /* put it back the way it was */
      patch({
        done: todo.done,
        doneByMe: todo.doneByMe,
        doneCount: todo.doneCount,
        status: todo.status,
      });
      throw err;
    }
  },

  /* Dropped from the list once the server confirms — no refetch needed, since
     removing a task doesn't move any other one, and the rest keep their order. */
  deleteTodo: async (networkId, todoId) => {
    await todosService.remove(networkId, todoId);
    set({ todos: get().todos.filter((t) => t.id !== todoId) });
  },
}));

/* Tasks under their group heading: General first, then each event in the order
   the API returned them. Inside a group, the rank fixed at load time — see
   withDisplayOrder above for why ticking a box must not move a row. */
const bySeq = (a, b) => (a.seq ?? 0) - (b.seq ?? 0);

export const selectGroups = (s) => {
  const order = [];
  s.todos.forEach((t) => {
    if (!order.some((g) => g.id === t.groupId)) {
      /* name can be blank for an event — the page labels it from its own event
         list, so nothing here invents a heading */
      order.push({ id: t.groupId, name: t.groupId ? t.groupName : GENERAL });
    }
  });
  order.sort((a, b) => (a.id === '' ? -1 : b.id === '' ? 1 : 0));
  return order.map((g) => ({
    ...g,
    tasks: s.todos.filter((t) => t.groupId === g.id).sort(bySeq),
  }));
};

export default useTodosStore;
